import { useQuery } from "@tanstack/react-query";
import { Award, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  PageFrame,
  ListResultsCard,
  WalletHeroCard,
  WalletHeroChip,
} from "@/components/common";
import { EmptyState } from "@/components/ui";
import { bookingService } from "@/services/bookingService";
import { formatDateTime } from "@/lib/datetime";

export default function PointsPage() {
  const { t } = useTranslation("points");
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["customer-points"],
    queryFn: () => bookingService.getCustomerPointsSummary(),
  });

  return (
    <PageFrame
      title={t("title")}
      description={t("description")}
      isLoading={isLoading}
      loadingMessage={t("loading")}
      error={!!error || !data}
      errorMessage={t("errorLoad")}
      onRetry={() => refetch()}
    >
      {data ? (
        <div className="grid items-start gap-5 lg:grid-cols-3">
          <div className="lg:sticky lg:top-24">
            <WalletHeroCard
              label={t("currentBalance")}
              amount={String(data.points_balance)}
              isAmountOwed={false}
              icon={Award}
              meta={<WalletHeroChip>{t("pointsLabel")}</WalletHeroChip>}
            />
          </div>

          <ListResultsCard
            className="lg:col-span-2"
            title={t("recentTitle")}
            contentClassName="p-0"
            isEmpty={data.transactions.length === 0}
            emptyState={
              <div className="p-5">
                <EmptyState
                  icon={Award}
                  title={t("emptyTitle")}
                  description={t("emptyDescription")}
                  size="md"
                />
              </div>
            }
          >
            <ul className="divide-surface-100 divide-y">
              {data.transactions.map((tx) => (
                <li
                  key={tx.id}
                  className="hover:bg-surface-50 dark:hover:bg-surface-100/50 flex items-center justify-between gap-4 px-5 py-3 transition-colors"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="bg-success-50 text-success-600 ring-success-600/15 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset">
                      <Sparkles
                        className="h-4 w-4"
                        aria-hidden
                      />
                    </span>
                    <div className="min-w-0">
                      <p className="text-surface-900 truncate text-sm font-medium">
                        {tx.reason}
                      </p>
                      <p className="text-surface-500 text-xs">
                        {formatDateTime(tx.created_at)}
                      </p>
                    </div>
                  </div>
                  <span className="bg-success-50 text-success-700 ring-success-600/15 inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums ring-1 ring-inset">
                    +{tx.points}
                  </span>
                </li>
              ))}
            </ul>
          </ListResultsCard>
        </div>
      ) : null}
    </PageFrame>
  );
}
